import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";

import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";

const API_USERS = "/api/users/";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: 500 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

function passwordCriteria(pwd) {
  return {
    length: pwd.length >= 12,
    letter: /[A-Za-z]/.test(pwd),
    digit: /\d/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
}

function PasswordStrength({ password }) {
  if (!password) return null;
  const c = passwordCriteria(password);
  const items = [
    { key: "length", label: "12 caractères minimum" },
    { key: "letter", label: "Une lettre" },
    { key: "digit", label: "Un chiffre" },
    { key: "special", label: "Un caractère spécial" },
  ];
  return (
    <MDBox mt={0.5} mb={1} pl={0.5}>
      {items.map(({ key, label }) => (
        <MDBox key={key} display="flex" alignItems="center" gap={0.5}>
          <Icon sx={{ fontSize: "14px !important", color: c[key] ? "#4caf50" : "#bdbdbd" }}>
            {c[key] ? "check_circle" : "radio_button_unchecked"}
          </Icon>
          <MDTypography variant="caption" sx={{ color: c[key] ? "#4caf50" : "#9e9e9e" }}>
            {label}
          </MDTypography>
        </MDBox>
      ))}
    </MDBox>
  );
}

PasswordStrength.propTypes = {
  password: PropTypes.string.isRequired,
};

function Utilisateurs() {
  const { user: currentUser } = useContext(AuthContext);
  const isAdminGlobal = currentUser?.profile?.roles_details?.some(
    (r) => r.role_name === "ADMIN_GLOBAL"
  );
  const isAdminInstitution = currentUser?.profile?.roles_details?.some(
    (r) => r.role_name === "ADMIN_INSTITUTION"
  );
  const canToggleUsers = isAdminGlobal || isAdminInstitution;

  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [filterInstitution, setFilterInstitution] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const [formData, setFormData] = useState({
    id: null,
    nom: "",
    description: "",
    email: "",
    password: "",
    passwordConfirmation: "",
    role_id: "",
    institution_id: "",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [resUsers, resRoles, resInstitutions] = await Promise.all([
        api.get(API_USERS),
        api.get("/api/roles/").catch(() => ({ data: [] })),
        api.get("/api/institutions/").catch(() => ({ data: [] })),
      ]);
      setRows(resUsers.data);
      setRoles(resRoles.data);
      setInstitutions(resInstitutions.data);
    } catch {
      setSnackbar({ open: true, message: "Erreur de chargement des données.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = () => {
    setIsEdit(false);
    setFormData({
      id: null,
      nom: "",
      description: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      role_id: isAdminInstitution ? roles.find((r) => r.name === "UTILISATEUR")?.id || "" : "",
      institution_id: isAdminInstitution
        ? currentUser?.profile?.roles_details?.find((r) => r.role_name === "ADMIN_INSTITUTION")
            ?.entite?.id || ""
        : "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsSubmitting(false);
  };

  const handleEdit = useCallback((rowData) => {
    setIsEdit(true);
    setFormData({
      id: rowData.id,
      nom: rowData.nom,
      description: rowData.description || "",
      email: rowData.email,
      password: "",
      passwordConfirmation: "",
      role_id: rowData.role_id || "",
      institution_id: rowData.institution_id || "",
    });
    setOpen(true);
  }, []);

  const openConfirm = useCallback(
    (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm }),
    []
  );

  const handleToggleActif = useCallback(
    (id, currentIsActive) => {
      const action = currentIsActive ? "désactiver" : "activer";
      openConfirm(`Voulez-vous vraiment ${action} cet utilisateur ?`, async () => {
        try {
          const res = await api.patch(`${API_USERS}${id}/toggle-active`);
          setSnackbar({ open: true, message: res.data.detail, severity: "success" });
          fetchData();
        } catch (error) {
          const msg = error.response?.data?.detail || "Erreur lors de l'action.";
          setSnackbar({ open: true, message: msg, severity: "error" });
        }
      });
    },
    [openConfirm, fetchData]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const needsPassword = !isEdit || formData.password;
    if (needsPassword) {
      if (formData.password !== formData.passwordConfirmation) {
        setSnackbar({
          open: true,
          message: "Les mots de passe ne correspondent pas.",
          severity: "error",
        });
        return;
      }
      const c = passwordCriteria(formData.password);
      if (!c.length || !c.letter || !c.digit || !c.special) {
        setSnackbar({
          open: true,
          message: "Le mot de passe ne respecte pas tous les critères de sécurité.",
          severity: "warning",
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = {
        nom: formData.nom,
        description: formData.description,
        email: formData.email,
        role_id: parseInt(formData.role_id, 10),
        institution_id: formData.institution_id ? parseInt(formData.institution_id, 10) : null,
        ...(formData.password ? { password: formData.password } : {}),
      };

      if (isEdit) {
        await api.put(`${API_USERS}${formData.id}`, payload);
        setSnackbar({ open: true, message: "Utilisateur modifié !", severity: "success" });
      } else {
        await api.post(API_USERS, payload);
        setSnackbar({ open: true, message: "Utilisateur ajouté !", severity: "success" });
      }
      handleClose();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = "Erreur lors de l'opération.";
      if (Array.isArray(detail)) {
        message = detail.map((err) => err.msg).join(" | ");
      } else if (typeof detail === "string") {
        message = detail;
      }
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredRows =
    !isAdminGlobal || filterInstitution === ""
      ? rows
      : filterInstitution === "__global__"
      ? rows.filter((r) => !r.institution_id)
      : rows.filter((r) => String(r.institution_id) === filterInstitution);

  const columns = useMemo(
    () => [
      {
        Header: "Utilisateur",
        accessor: "nom",
        width: "40%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <MDBox>
            {/* eslint-disable-next-line react/prop-types */}
            <MDTypography variant="button" fontWeight="medium" display="block">
              {/* eslint-disable-next-line react/prop-types */}
              {row.original.nom}
            </MDTypography>
            {/* eslint-disable-next-line react/prop-types */}
            {row.original.role_name && (
              <MDTypography variant="caption" color="text" display="block">
                {/* eslint-disable-next-line react/prop-types */}
                {row.original.role_name}
              </MDTypography>
            )}
            {/* eslint-disable-next-line react/prop-types */}
            {row.original.institution_name && (
              <MDTypography variant="caption" color="secondary" display="block">
                {/* eslint-disable-next-line react/prop-types */}
                {row.original.institution_name}
              </MDTypography>
            )}
          </MDBox>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        width: "30%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <MDTypography variant="caption" sx={{ wordBreak: "break-all", display: "block" }}>
            {value}
          </MDTypography>
        ),
      },
      {
        Header: "Statut",
        accessor: "is_active",
        width: "10%",
        // eslint-disable-next-line react/prop-types
        Cell: ({ value }) => (
          <MDBadge
            badgeContent={value ? "Actif" : "Inactif"}
            color={value ? "success" : "secondary"}
            variant="gradient"
            size="sm"
          />
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <MDBox display="flex" gap={0.5}>
            <MDButton
              variant="contained"
              color="info"
              size="small"
              // eslint-disable-next-line react/prop-types
              onClick={() => handleEdit(row.original)}
            >
              Modifier
            </MDButton>
            {/* eslint-disable-next-line react/prop-types */}
            {canToggleUsers && String(row.original.id) !== String(currentUser?.profile?.id) && (
              <MDButton
                variant="contained"
                // eslint-disable-next-line react/prop-types
                color={row.original.is_active ? "warning" : "success"}
                size="small"
                // eslint-disable-next-line react/prop-types
                onClick={() => handleToggleActif(row.original.id, row.original.is_active)}
              >
                {/* eslint-disable-next-line react/prop-types */}
                {row.original.is_active ? "Désactiver" : "Activer"}
              </MDButton>
            )}
          </MDBox>
        ),
      },
    ],
    [handleEdit, handleToggleActif, canToggleUsers, currentUser]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Gestion des Utilisateurs
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
                  >
                    Gestion des comptes et des accès utilisateurs
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" onClick={handleOpen}>
                  Ajouter un Utilisateur
                </MDButton>
              </MDBox>

              {isAdminGlobal && (
                <MDBox px={3} pt={3} pb={1}>
                  <TextField
                    select
                    size="small"
                    label="Filtrer par institution"
                    value={filterInstitution}
                    onChange={(e) => setFilterInstitution(e.target.value)}
                    sx={{ minWidth: 260 }}
                  >
                    <MenuItem value="">Toutes les institutions</MenuItem>
                    <MenuItem value="__global__">Sans institution (Global)</MenuItem>
                    {institutions.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.nom}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>
              )}

              <MDBox pt={isAdminGlobal ? 1 : 3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: filteredRows }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal d'ajout / modification */}
      <Modal open={open} onClose={() => !isSubmitting && handleClose()}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.3s ease-out both` }}>
          <MDTypography variant="h6" mb={2}>
            {isEdit ? "Modifier un Utilisateur" : "Ajouter un Utilisateur"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Nom complet"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                fullWidth
                label={isEdit ? "Mot de passe (laisser vide si inchangé)" : "Mot de passe"}
                type="password"
                required={!isEdit}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </MDBox>
            <PasswordStrength password={formData.password} />
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type="password"
                required={!isEdit || !!formData.password}
                value={formData.passwordConfirmation}
                onChange={(e) => setFormData({ ...formData, passwordConfirmation: e.target.value })}
                error={
                  !!formData.passwordConfirmation &&
                  formData.password !== formData.passwordConfirmation
                }
                helperText={
                  formData.passwordConfirmation &&
                  formData.password !== formData.passwordConfirmation
                    ? "Les mots de passe ne correspondent pas"
                    : ""
                }
              />
            </MDBox>
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Rôle"
                    required
                    value={formData.role_id}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  >
                    {roles
                      .filter((r) =>
                        isAdminInstitution
                          ? ["UTILISATEUR", "GESTIONNAIRE", "CONTROLEUR"].includes(r.name)
                          : r.name !== "ADMIN_GLOBAL"
                      )
                      .map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Institution"
                    required
                    value={formData.institution_id}
                    onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
                    disabled={isAdminInstitution}
                  >
                    {institutions
                      .filter((inst) =>
                        isAdminInstitution
                          ? inst.id ===
                            (currentUser?.profile?.roles_details?.find(
                              (r) => r.role_name === "ADMIN_INSTITUTION"
                            )?.entite?.id || null)
                          : true
                      )
                      .map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          {inst.nom}
                        </MenuItem>
                      ))}
                  </TextField>
                </MDBox>
              </Grid>
            </Grid>
            <MDBox mt={2} display="flex" justifyContent="flex-end">
              <MDButton
                onClick={handleClose}
                color="secondary"
                sx={{ mr: 1 }}
                disabled={isSubmitting}
              >
                Annuler
              </MDButton>
              <MDButton
                type="submit"
                variant="gradient"
                color="purple"
                disabled={isSubmitting}
                endIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
              >
                {isEdit ? "Modifier" : "Ajouter"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>

      {/* Dialog de confirmation (remplace window.confirm) */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Utilisateurs;
